import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { preloadImage } from '../../shared/utils/imageLoader';
import { db, storage } from '../../shared/api/firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, collection, addDoc, query, orderBy, limit, getDocs, Timestamp, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import drumSound from '../../assets/music/Drum.mp3';
import symbolsSound from '../../assets/music/Symbols.mp3';
import { ArrowLeft } from '../../components/common/Icons';
import { generateSayingExplanation } from '../../services/aiAnalysisService.js';
import styles from './SayingPaperPage.module.css';
import '../../styles/contents/dailySaying/SayingPaperPage.css';
import HowToUseSayingModal from '../../components/Modals/HowToUseSayingModal.js';
import LoginPromptModal from '../../components/common/LoginPromptModal.js';
import { ClipboardPenLine } from 'lucide-react';

// 스타일 직접 import
// import '../../styles/teachering-components.css';
// import '../../styles/teachering-pages.css';

const SayingPaperContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 스토리지 키들
  const CURRENT_SAYING_KEY = 'current_saying_paper';
  
  // 초기 명언 데이터 (localStorage에서 복원 또는 location.state에서 가져옴)
  const getInitialSaying = () => {
    let sayingData = null;
    if (location.state?.saying) {
      sayingData = location.state.saying;
      try {
        localStorage.setItem(CURRENT_SAYING_KEY, JSON.stringify(sayingData));
      } catch (error) {
        console.error('명언 데이터 저장 실패:', error);
      }
    } else {
      try {
        const stored = localStorage.getItem(CURRENT_SAYING_KEY);
        if (stored) {
          sayingData = JSON.parse(stored);
        }
      } catch (error) {
        console.error('저장된 명언 데이터 복원 실패:', error);
      }
    }
    return sayingData || { quote: "명언이 선택되지 않았습니다.", author: "미상" };
  };

  const [saying, setSaying] = useState(getInitialSaying);
  const [authorImage, setAuthorImage] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAuthorInfo, setShowAuthorInfo] = useState(true);
  const [authorInfo, setAuthorInfo] = useState(null);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isContentReady, setIsContentReady] = useState(false);
  const [imageLoadComplete, setImageLoadComplete] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [isExplanationLoading, setIsExplanationLoading] = useState(false);
  const [isHowToUseModalOpen, setIsHowToUseModalOpen] = useState(false);
  const [isLoginPromptModalOpen, setIsLoginPromptModalOpen] = useState(false);
  const drumAudioRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 페이지 로딩 시작 시 Drum 소리 재생
  useEffect(() => {
    try {
      const audio = new Audio(drumSound);
      audio.volume = 0.2;
      audio.loop = true;
      audio.play().catch(e => {});
      drumAudioRef.current = audio;
    } catch (error) {
    }
    return () => {
      if (drumAudioRef.current) {
        drumAudioRef.current.pause();
        drumAudioRef.current = null;
      }
    };
  }, []);

  // 현재 사용자 ID 가져오기
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => setUserId(user ? user.uid : null));
    return () => unsubscribe();
  }, []);

  // Firebase Storage에서 저자 이미지 가져오기
  useEffect(() => {
    const fetchAuthorImage = async () => {
      if (!saying) return;
      const authorName = saying?.name || saying?.author;
      if (!authorName || ['미상', '익명', '작자미상'].includes(authorName.trim())) {
        setImageLoadComplete(true);
        setIsLoading(false);
        return;
      }
      
      setIsLoadingImage(true);
      try {
        const authorRef = doc(db, 'great_man', authorName);
        const authorDoc = await getDoc(authorRef);
        if (authorDoc.exists() && authorDoc.data().imageUrl) {
          await loadImage(authorDoc.data().imageUrl, authorName);
        } else {
          await tryStorageImage(authorName);
        }
      } catch (error) {
        console.error(`저자 이미지 처리 중 오류: ${authorName}`, error);
        setAuthorImage(null);
        setImageLoadComplete(true);
      } finally {
        setIsLoadingImage(false);
      }
    };

    const loadImage = (url, name) => {
      return new Promise((resolve) => {
        preloadImage(
          url,
          () => { setAuthorImage(url); setImageLoadComplete(true); resolve(); },
          () => { tryStorageImage(name).then(resolve); }
        );
      });
    };

    const tryStorageImage = async (name) => {
      try {
        const url = await getDownloadURL(ref(storage, `${name}.png`));
        await new Promise(resolve => preloadImage(url, () => { setAuthorImage(url); resolve(); }, resolve));
      } catch {
        setAuthorImage(null);
      } finally {
        setImageLoadComplete(true);
      }
    };
    
    fetchAuthorImage();
  }, [saying]);

  // Firebase Firestore에서 인물 정보 가져오기
  useEffect(() => {
    const fetchAuthorInfo = async () => {
      if (!saying || !saying.author || ['미상', '익명', '작자미상'].includes(saying.author.trim())) {
        return;
      }
      setIsLoadingInfo(true);
      try {
        const authorRef = doc(db, 'great_man', saying.author);
        const authorDoc = await getDoc(authorRef);
        setAuthorInfo(authorDoc.exists() ? authorDoc.data() : null);
      } catch (error) {
        console.error(`저자 정보 가져오기 실패: ${saying.author}`, error);
        setAuthorInfo(null);
      } finally {
        setIsLoadingInfo(false);
      }
    };
    fetchAuthorInfo();
  }, [saying]);

  // 콘텐츠 준비 완료 확인
  useEffect(() => {
    if (imageLoadComplete && !isLoadingInfo) {
      const timer = setTimeout(() => {
        setIsContentReady(true);
        setIsLoading(false);
        if (drumAudioRef.current) {
          drumAudioRef.current.pause();
        }
        try {
          const symbolsAudio = new Audio(symbolsSound);
          symbolsAudio.volume = 0.4;
          symbolsAudio.play().catch(e => {});
        } catch (error) {
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [imageLoadComplete, isLoadingInfo]);

  // 명언 해설 생성
  useEffect(() => {
    const getExplanation = async () => {
      if (saying && saying.author && saying.quote && !['미상', '익명', '작자미상'].includes(saying.author.trim())) {
        setIsExplanationLoading(true);
        const result = await generateSayingExplanation({ author: saying.author, quote: saying.quote });
        setExplanation(result.explanation);
        setIsExplanationLoading(false);
      }
    };
    getExplanation();
  }, [saying]);

  // 마지막 인물 정보 펼치기 날짜 가져오기 (daily_saying 컬렉션에서)
  const getLastInfoExpandDate = async (userId) => {
    try {
      if (!userId) return null;
      const dailySayingCollectionRef = collection(db, 'users', userId, 'daily_saying');
      const q = query(dailySayingCollectionRef, orderBy('used_at', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const lastData = querySnapshot.docs[0].data();
        if (lastData && lastData.info_expand_time) {
          return lastData.info_expand_time.toDate();
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // 날짜가 같은지 비교 (00시 기준)
  const isSameDay = (date1, date2) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  // 인물 정보 펼치기 기록 저장 (daily_saying 컬렉션 내에)
  const recordInfoExpand = async () => {
    if (!userId) return;
    try {
      const now = new Date();
      const koreaTime = new Date(now.getTime() + (9 * 60 * 60 * 1000));
      const lastExpandDate = await getLastInfoExpandDate(userId);
      if (lastExpandDate && !isSameDay(lastExpandDate, koreaTime)) {
        const analytics = getAnalytics();
        logEvent(analytics, 'daily_saying_expand_toggle', {
          user_id: userId,
          author: saying.author,
        });
      }
      
      const dailySayingCollectionRef = collection(db, 'users', userId, 'daily_saying');
      const q = query(dailySayingCollectionRef, orderBy('used_at', 'desc'), limit(1));
      const querySnapshot = await getDocs(q);
      
      const record = {
        info_expand_time: Timestamp.fromDate(koreaTime),
        info_expand_time_string: koreaTime.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
      };

      if (!querySnapshot.empty) {
        const docRef = doc(db, 'users', userId, 'daily_saying', querySnapshot.docs[0].id);
        await updateDoc(docRef, record);
      } else {
        await addDoc(dailySayingCollectionRef, { ...record, used_at: Timestamp.fromDate(koreaTime) });
      }
    } catch (error) {
      console.error('인물 정보 펼치기 기록 중 오류 발생:', error);
    }
  };

  const handleImageClick = () => setShowModal(true);
  const closeModal = () => setShowModal(false);
  const toggleAuthorInfo = () => {
    if (!showAuthorInfo) recordInfoExpand();
    setShowAuthorInfo(!showAuthorInfo);
  };
  const toggleExplanation = () => {
    setShowExplanation(!showExplanation);
  };
  
  const handleNewSaying = () => navigate('/play/daily-saying');
  
  const formattedDate = `${new Date().getFullYear()}년 ${new Date().getMonth() + 1}월 ${new Date().getDate()}일`;

  return (
      <section className="daily-saying-paper-content-start-section">
          <div className={`container ${styles.pageContainer}`}>
              <div className={`${styles.paper} ${isLoading ? styles.paperLoading : ''}`}>
                  <div className={styles.topButtonContainer}>
                    <button onClick={handleNewSaying} className={styles.backButton}>
                        <ArrowLeft size={14} />
                        뒤로가기
                    </button>
                    <button className="btn-simple" onClick={() => setIsHowToUseModalOpen(true)}>
                      <ClipboardPenLine />
                      <span>활용하기</span>
                    </button>
                  </div>
                  <h1 className={styles.title}>오늘의 명언</h1>
                  <div className={styles.date}>{formattedDate}</div>
                  {isLoading ? (
                      <div className={styles.loadingContainer}>
                          <div className={styles.loadingIcon}>🎯</div>
                          <div className={styles.loadingText}>오늘의 명언을<br />불러오는 중...</div>
                      </div>
                  ) : (
                      <>
                          {authorImage && (
                              <div className={styles.authorImageContainer}>
                                  <img src={authorImage} alt={saying.author} className={styles.authorImage} onClick={handleImageClick} title="클릭하면 크게 볼 수 있습니다" />
                              </div>
                          )}
                          <div className={styles.author}>{saying.author}</div>
                          <div className={styles.quote}>"{saying.quote || saying.content}"</div>
                          {saying && saying.author && !['미상', '익명', '작자미상'].includes(saying.author.trim()) && (
                              <button className={`${styles.infoButton} ${styles.infoButtonExplanation}`} onClick={toggleExplanation}>
                                  {showExplanation ? '명언 해설 접기 ▲' : '명언 해설 보기 ▼'}
                              </button>
                          )}
                          <div className={`${styles.infoPanel} ${styles.infoPanelExplanation} ${showExplanation ? styles.infoPanelExpanded : styles.infoPanelCollapsed}`}>
                              <div className={styles.infoPanelContent}>
                                  {isExplanationLoading ? <p>AI가 명언을 해석하는 중입니다...</p> : <p className={`${styles.infoItem} ${styles.explanationText}`}>{explanation}</p>}
                              </div>
                          </div>
                          {saying && saying.author && !['미상', '익명', '작자미상'].includes(saying.author.trim()) && (
                            <>
                              <div className={styles.infoSeparator} />
                              <div className={`${styles.infoPanel} ${styles.infoPanelExpanded}`}>
                                  <div className={styles.infoPanelContent}>
                                      {isLoadingInfo ? <p>인물 정보를 불러오는 중...</p> : (
                                          <div>
                                              <h3>{saying.author}</h3>
                                              {saying.nationality && <div><span className={styles.infoLabel}>국적:</span><span className={styles.infoItem}>{saying.nationality}</span></div>}
                                              {saying.birth && <div><span className={styles.infoLabel}>출생:</span><span className={styles.infoItem}>{saying.birth}</span></div>}
                                              {saying.death && <div><span className={styles.infoLabel}>사망:</span><span className={styles.infoItem}>{saying.death}</span></div>}
                                              {saying.achievement && <div><span className={styles.infoLabel}>업적:</span><span className={styles.infoItem}>{saying.achievement}</span></div>}
                                          </div>
                                      )}
                                  </div>
                              </div>
                            </>
                          )}
                      </>
                  )}
                  <div className={styles.paperTornEdge}></div>
              </div>
              {showModal && authorImage && (
                  <div className={styles['author-image-modal-background']} onClick={closeModal}>
                      <div className={styles['author-image-modal-close-button']} onClick={closeModal}>×</div>
                      <img src={authorImage} alt={saying.author} className={styles['author-image-modal-image']} onClick={(e) => e.stopPropagation()} />
                  </div>
              )}
          </div>
          <HowToUseSayingModal 
            isOpen={isHowToUseModalOpen}
            onClose={() => setIsHowToUseModalOpen(false)}
            onRequireLogin={() => setIsLoginPromptModalOpen(true)}
          />
          <LoginPromptModal
            isOpen={isLoginPromptModalOpen}
            onClose={() => setIsLoginPromptModalOpen(false)}
            featureName="활동지 다운로드는"
          />
      </section>
  );
};

const SayingPaperPage = () => {
  const location = useLocation();
  const saying = location.state?.saying;
  // 명언 객체가 존재하면 고유한 key를 생성, 없으면 현재 시간을 key로 사용
  const sayingKey = saying ? `${saying.author}-${saying.quote}` : Date.now();

  return <SayingPaperContent key={sayingKey} />;
};

export default SayingPaperPage; 